import { assertRequiredColumns } from '../assertRequiredColumns';
import type {
  DataTableAdapterConfig,
  DataTableAdapterProps,
  DataTableColumnDef,
  RenderAdapterResult,
} from '../types';
import type { TransformRow } from '../../transforms/types';
import { validateRowsInput } from '../validateRowsInput';

const DEFAULT_PAGE_SIZE = 25;

export function dataTableColumnDependencies(
  columns: readonly DataTableColumnDef[],
): string[] {
  const names: string[] = [];
  for (const column of columns) {
    names.push(column.accessorKey);
    if (column.formatByDimension?.dimensionKey) {
      names.push(column.formatByDimension.dimensionKey);
    }
  }
  return Array.from(new Set(names));
}

function tableProps(
  config: DataTableAdapterConfig | undefined,
  data: TransformRow[],
): DataTableAdapterProps {
  const columns = [...(config?.columns ?? [])];
  const searchColumns = Array.from(
    new Set([
      ...(config?.presentation?.searchColumns ?? []),
      ...columns
        .filter((column) => column.searchable === true)
        .map((column) => column.accessorKey),
    ]),
  );

  return {
    ...config?.presentation?.passthrough,
    data,
    columns,
    mode: config?.presentation?.mode ?? 'paginated',
    pageSize: config?.presentation?.pageSize ?? DEFAULT_PAGE_SIZE,
    searchColumns: searchColumns.length > 0 ? [...searchColumns] : undefined,
    searchLabel: config?.presentation?.searchLabel,
  };
}

export function dataTableAdapter(
  rows: unknown,
  config: DataTableAdapterConfig,
): RenderAdapterResult<DataTableAdapterProps> {
  const validated = validateRowsInput(rows, tableProps(config, []));
  if ('props' in validated) {
    return validated;
  }

  const columnError = assertRequiredColumns(
    validated.rows,
    dataTableColumnDependencies(config.columns),
  );
  if (columnError) {
    return { props: tableProps(config, []), error: columnError };
  }

  return { props: tableProps(config, validated.rows) };
}
